<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { GlbLoaderError, GlbLoaderStatus } from '@/composables/useGlbLoader'

const props = defineProps<{
  status: GlbLoaderStatus
  error: GlbLoaderError | null
  progress: number | null
  hasModel: boolean
  converting?: boolean
  conversionFailed?: boolean
  dataLoading?: boolean
}>()

const { t } = useI18n()

const loadingPercent = computed(() =>
  props.progress === null ? null : Math.round(props.progress * 100),
)
</script>

<template>
  <div v-if="conversionFailed" class="overlay overlay--failed">
    <v-icon size="32" color="#c62828">error_outline</v-icon>
    <span>{{ t('component.bopModelViewer.conversionFailed') }}</span>
  </div>

  <div v-else-if="converting" class="overlay overlay--converting">
    <v-progress-circular indeterminate color="primary" />
    <span>{{ t('component.bopModelViewer.converting') }}</span>
    <span class="overlay--converting__hint">
      {{ t('component.bopModelViewer.convertingHint') }}
    </span>
  </div>

  <div
    v-if="status === 'idle' && !hasModel && !converting && !conversionFailed && !dataLoading"
    class="overlay overlay--empty"
  >
    {{ t('component.bopModelViewer.noModel') }}
  </div>

  <div v-if="dataLoading && status === 'idle' && !hasModel" class="overlay overlay--loading">
    <v-progress-circular indeterminate color="primary" />
  </div>

  <div v-if="status === 'loading'" class="overlay overlay--loading-model">
    <v-progress-circular
      :indeterminate="loadingPercent === null"
      :model-value="loadingPercent ?? 0"
      color="primary"
    />
    <span>{{ t('component.bopModelViewer.loadingModel') }}</span>
  </div>

  <div v-if="status === 'error'" class="overlay overlay--error">
    {{ t(`component.bopModelViewer.${error ?? 'modelFailed'}`) }}
  </div>
</template>

<style scoped>
.overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay--converting {
  flex-direction: column;
  gap: 1em;
  color: rgba(0, 0, 0, 0.6);
  text-align: center;
  padding: 1em;
  pointer-events: none;
}

.overlay--converting__hint {
  font-size: 0.8em;
  color: rgba(0, 0, 0, 0.4);
}

.overlay--failed {
  flex-direction: column;
  gap: 0.5em;
  color: #c62828;
  text-align: center;
  padding: 1em;
  pointer-events: none;
}

.overlay--empty {
  color: rgba(0, 0, 0, 0.4);
  text-align: center;
  padding: 1em;
  pointer-events: none;
}

.overlay--loading {
  pointer-events: none;
}

.overlay--loading-model {
  flex-direction: column;
  gap: 1em;
  background: rgba(0, 0, 0, 0.6);
  color: rgba(255, 255, 255, 0.8);
}

.overlay--error {
  color: #c62828;
  text-align: center;
  padding: 1em;
  pointer-events: none;
  font-size: 0.85em;
}
</style>
